<template>
  <div class="flex flex-col min-h-screen bg-white dark:bg-neutral-dark">
    <!-- Mobil Menü Overlay -->
    <div v-if="isMenuOpen" 
         class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] lg:hidden"
         @click="isMenuOpen = false">
    </div>

    <!-- Mobil Menü Drawer -->
    <div class="fixed inset-y-0 right-0 w-64 bg-white dark:bg-neutral-dark lg:hidden transform transition-transform duration-300 ease-in-out z-[70] shadow-2xl"
         :class="isMenuOpen ? 'translate-x-0' : 'translate-x-full'">
      <div class="flex flex-col h-full">
        <div class="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <span class="text-gray-900 dark:text-white font-semibold">Menü</span>
          <button @click="isMenuOpen = false" 
                  class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-colors">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        <nav class="flex-1 overflow-y-auto py-4">
          <div class="space-y-1 px-3">
            <template v-for="item in menuItems" :key="item.path">
              <div class="relative">
                <div class="flex items-center justify-between w-full px-4 py-2.5 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                     :class="{ 'text-primary dark:text-white': item.isOpen }"
                     @click="toggleMobileMenu(item)">
                  <span>{{ item.title }}</span>
                  <ChevronDownIcon v-if="item.children && item.path !== localePath('companies')" 
                                   class="w-4 h-4 transition-transform duration-200"
                                   :class="{ 'rotate-180': item.isOpen }" />
                </div>

                <!-- Birinci Seviye Alt Menü -->
                <div v-if="item.children && item.path !== localePath('companies')" 
                     class="pl-4 mt-1 space-y-1 overflow-hidden transition-all duration-300"
                     :class="{ 'max-h-0': !item.isOpen, 'max-h-[500px]': item.isOpen }">
                  <template v-for="child in item.children" :key="child.path">
                    <div class="relative">
                      <div class="flex items-center justify-between w-full px-4 py-2.5 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                           :class="{ 'text-primary dark:text-white': child.isOpen }"
                           @click="toggleMobileSubmenu(child)">
                        <span>{{ child.title }}</span>
                        <ChevronDownIcon v-if="child.children" 
                                        class="w-4 h-4 transition-transform duration-200"
                                        :class="{ 'rotate-180': child.isOpen }" />
                      </div>

                      <!-- İkinci Seviye Alt Menü -->
                      <div v-if="child.children" 
                           class="pl-4 mt-1 space-y-1 overflow-hidden transition-all duration-300"
                           :class="{ 'max-h-0': !child.isOpen, 'max-h-[500px]': child.isOpen }">
                        <NuxtLink v-for="subChild in child.children"
                                  :key="subChild.path"
                                  :to="subChild.path"
                                  class="block px-4 py-2.5 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                                  @click="isMenuOpen = false">
                          {{ subChild.title }}
                        </NuxtLink>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </nav>
        <div class="p-4 border-t border-gray-100 dark:border-white/10">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-2">
              <span :class="`fi fi-${locale === 'tr' ? 'tr' : 'gb'} rounded-sm`"></span>
              <span class="text-gray-600 dark:text-white/90">{{ currentLocaleName }}</span>
            </div>
            <button @click="toggleColorMode" 
                    class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-colors">
              <SunIcon v-if="colorMode.value === 'dark'" class="w-5 h-5" />
              <MoonIcon v-else class="w-5 h-5" />
            </button>
          </div>
          <div class="space-y-1">
            <button v-for="loc in availableLocales" 
                    :key="loc.code"
                    @click="switchLocale(loc.code)"
                    class="flex items-center space-x-3 w-full px-4 py-2.5 rounded-lg text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
              <span :class="`fi fi-${loc.code === 'tr' ? 'tr' : 'gb'} rounded-sm`"></span>
              <span>{{ loc.name }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <header class="bg-white dark:bg-neutral-dark fixed w-full z-50 transition-all duration-300" 
            :class="{ 'shadow-lg': scrolled }">
      <div class="bg-primary">
        <div class="container mx-auto px-4 h-10 flex justify-between items-center">
          <div class="text-white/90 text-sm hidden sm:block">
            <span class="mr-4">
              <i class="fas fa-phone mr-2"></i>
              {{ contactInfo.phone }}
            </span>
            <span>
              <i class="fas fa-envelope mr-2"></i>
              {{ contactInfo.email }}
            </span>
          </div>
          <div class="flex items-center space-x-4">
            <a href="#" @click.prevent="startTransition($event)" 
               class="text-white/90 hover:text-white transition-colors flex items-center space-x-2">
              <img src="@/assets/images/pomegranate.png" alt="Nar İkonu" class="w-5 h-5" />
              <span class="text-[10px] sm:text-sm font-medium whitespace-nowrap">NAR KULUÇKA MERKEZİ</span>
            </a>
            <div class="w-px h-4 bg-white/20"></div>
            <a href="https://argeportal.com" target="_blank" rel="noopener noreferrer"
               class="text-white/90 hover:text-white transition-colors flex items-center space-x-2">
              <i class="fas fa-flask text-lg"></i>
              <span class="text-[10px] sm:text-sm font-medium whitespace-nowrap">ARGE PORTAL</span>
            </a>
            <div class="w-px h-4 bg-white/20"></div>
            <a v-if="socialMedia.facebook" :href="socialMedia.facebook" target="_blank" rel="noopener noreferrer" class="text-white/90 hover:text-white transition-colors">
              <i class="fab fa-facebook text-lg"></i>
            </a>
            <a v-if="socialMedia.twitter" :href="socialMedia.twitter" target="_blank" rel="noopener noreferrer" class="text-white/90 hover:text-white transition-colors">
              <i class="fab fa-twitter text-lg"></i>
            </a>
            <a v-if="socialMedia.linkedin" :href="socialMedia.linkedin" target="_blank" rel="noopener noreferrer" class="text-white/90 hover:text-white transition-colors">
              <i class="fab fa-linkedin text-lg"></i>
            </a>
            <a v-if="socialMedia.instagram" :href="socialMedia.instagram" target="_blank" rel="noopener noreferrer" class="text-white/90 hover:text-white transition-colors">
              <i class="fab fa-instagram text-lg"></i>
            </a>
          </div>
        </div>
      </div>
      <nav class="container mx-auto px-4 h-20 flex justify-between items-center">
        <div class="flex items-center space-x-8">
          <NuxtLink :to="localePath('index')" class="flex items-center">
            <img v-if="colorMode.value === 'dark'" :src="siteSettings.dark_logo || '/assets/images/tp-logo-dark.png'" :alt="siteSettings.site_title" class="h-10 w-auto" />
            <img v-else :src="siteSettings.logo || '/assets/images/tp-logo.png'" :alt="siteSettings.site_title" class="h-10 w-auto" />
          </NuxtLink>
          <div class="hidden lg:flex items-center space-x-2">
            <template v-for="(item, index) in menuItems" :key="index">
              <div class="relative group">
                <NuxtLink 
                  :to="item.path"
                  class="relative px-5 py-2.5 rounded-lg text-gray-700 dark:text-white/90 hover:text-primary dark:hover:text-white font-medium group transition-all duration-300 flex items-center">
                  <span class="relative z-10">{{ item.title }}</span>
                  <ChevronDownIcon v-if="item.children && item.path !== localePath('companies')" 
                                   class="w-4 h-4 ml-1 transition-transform duration-200 group-hover:rotate-180" />
                  <div class="absolute inset-0 h-full w-full scale-0 rounded-lg bg-gray-100 dark:bg-white/5 transition-all duration-300 group-hover:scale-100"></div>
                  <div class="absolute -bottom-0.5 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full"></div>
                </NuxtLink>

                <!-- Birinci Seviye Alt Menü -->
                <div v-if="item.children && item.path !== localePath('companies')" 
                     class="absolute left-0 mt-2 py-3 w-64 bg-white dark:bg-neutral-dark rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 delay-[50ms] transform origin-top-left group-hover:translate-y-0 translate-y-2 z-[100] border border-gray-100 dark:border-white/10 backdrop-blur-sm">
                  <template v-for="(child, childIndex) in item.children" :key="childIndex">
                    <div class="relative group/child">
                      <NuxtLink :to="child.path"
                                class="flex items-center justify-between px-5 py-3 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-all duration-300 relative group/item overflow-hidden">
                        <span class="relative z-[5]">{{ child.title }}</span>
                        <ChevronRightIcon v-if="child.children" class="w-4 h-4 transition-transform duration-300 group-hover/child:translate-x-1 relative z-[5]" />
                        <div class="absolute left-0 top-0 w-1 h-full bg-primary scale-y-0 group-hover/item:scale-y-100 transition-transform duration-300 origin-top z-[3]"></div>
                        <div class="absolute inset-0 w-full h-full bg-gray-50 dark:bg-white/5 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 z-[2]"></div>
                      </NuxtLink>

                      <!-- İkinci Seviye Alt Menü -->
                      <div v-if="child.children"
                           class="absolute left-full top-0 ml-2 py-3 w-64 bg-white dark:bg-neutral-dark rounded-xl shadow-xl opacity-0 invisible group-hover/child:opacity-100 group-hover/child:visible transition-all duration-300 delay-[50ms] transform -translate-x-2 group-hover/child:translate-x-0 border border-gray-100 dark:border-white/10 backdrop-blur-sm z-[110]">
                        <NuxtLink v-for="(subChild, subChildIndex) in child.children"
                                  :key="subChildIndex"
                                  :to="subChild.path"
                                  class="block px-5 py-3 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-all duration-300 relative group/subchild overflow-hidden">
                          <span class="relative z-[5]">{{ subChild.title }}</span>
                          <div class="absolute left-0 top-0 w-1 h-full bg-primary scale-y-0 group-hover/subchild:scale-y-100 transition-transform duration-300 origin-top z-[3]"></div>
                          <div class="absolute inset-0 w-full h-full bg-gray-50 dark:bg-white/5 opacity-0 group-hover/subchild:opacity-100 transition-opacity duration-300 z-[2]"></div>
                        </NuxtLink>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
        
        <div class="flex items-center space-x-4">
          <div class="relative group hidden lg:block" @mouseleave="isLangOpen = false">
            <button @mouseenter="isLangOpen = true"
                    class="flex items-center space-x-2 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10">
              <span :class="`fi fi-${locale === 'tr' ? 'tr' : 'gb'} rounded-sm`"></span>
              <span class="hidden md:inline">{{ currentLocaleName }}</span>
              <ChevronDownIcon class="w-4 h-4 transition-transform duration-200"
                              :class="{ 'rotate-180': isLangOpen }" />
            </button>
            <div class="absolute right-0 mt-1 py-2 w-48 bg-white dark:bg-neutral-dark rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-right group-hover:translate-y-0 translate-y-2 z-50"
                 @mouseenter="isLangOpen = true">
              <button v-for="loc in availableLocales" 
                      :key="loc.code"
                      @click="switchLocale(loc.code)"
                      class="flex items-center space-x-3 px-4 py-2.5 w-full text-left text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <span :class="`fi fi-${loc.code === 'tr' ? 'tr' : 'gb'} rounded-sm`"></span>
                <span>{{ loc.name }}</span>
              </button>
            </div>
          </div>

          <button @click="toggleColorMode" 
                  class="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-colors">
            <SunIcon v-if="colorMode.value === 'dark'" class="w-5 h-5" />
            <MoonIcon v-else class="w-5 h-5" />
          </button>

          <!-- Mobil menü butonu -->
          <button @click="isMenuOpen = !isMenuOpen" 
                  class="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/90 hover:text-primary dark:hover:text-white transition-colors">
            <Bars3Icon class="w-6 h-6" />
          </button>
        </div>
      </nav>
    </header>

    <main class="flex-grow pt-20">
      <slot />
    </main>

    <!-- Önemli Duyuru Modalı -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="showAnnouncementModal && hasActiveAnnouncements" class="fixed inset-0 z-[100] overflow-y-auto">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
             @click="closeAnnouncementModal"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-dark p-6 text-left shadow-xl transition-all">
            <!-- Üst Kısım - Başlık ve Kapat -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Önemli Duyuru
                </h3>
              </div>
              <button @click="closeAnnouncementModal" 
                      class="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <XMarkIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <!-- İçerik -->
            <div class="mt-4 space-y-4">
              <div class="bg-primary/5 dark:bg-primary/10 rounded-xl p-4 border-l-4 border-primary">
                <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {{ currentAnnouncement.title }}
                </h4>
                <p class="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                  {{ currentAnnouncement.description }}
                </p>
              </div>
              
              <div v-if="currentAnnouncement.date" class="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <CalendarIcon class="w-4 h-4 mr-2" />
                {{ currentAnnouncement.date }}
              </div>
            </div>

            <!-- Alt Kısım - Butonlar -->
            <div class="mt-6 flex flex-wrap items-center justify-between">
              <div class="flex items-center space-x-4">
                <button v-if="activeAnnouncements.length > 1 && currentAnnouncementIndex > 0"
                        @click="prevAnnouncement"
                        class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Önceki
                </button>
                <button v-if="activeAnnouncements.length > 1 && currentAnnouncementIndex < activeAnnouncements.length - 1"
                        @click="nextAnnouncement"
                        class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center">
                  Sonraki
                  <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>
              <div class="flex items-center space-x-4 mt-4 sm:mt-0">
                <label class="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                  <input type="checkbox" 
                         v-model="dontShowAgain"
                         class="rounded border-gray-300 dark:border-neutral-600 text-primary focus:ring-primary dark:bg-neutral-800" />
                  <span>Bu duyuruyu tekrar gösterme</span>
                </label>
                <button @click="closeAnnouncementModal"
                        class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                  Anladım
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Çerez Uyarısı -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform translate-y-full opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform translate-y-full opacity-0"
    >
      <div v-if="!cookiesAccepted" 
           class="fixed bottom-0 inset-x-0 z-50 bg-white dark:bg-neutral-dark border-t border-gray-200 dark:border-neutral-700 shadow-lg transform transition-all duration-300">
        <div class="container mx-auto px-4 py-4">
          <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div class="flex items-start space-x-4">
              <div class="text-primary dark:text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-gray-600 dark:text-gray-300 text-sm">
                  {{ t('cookies.description') }}
                  <a href="#" @click.prevent="showPrivacyModal = true" class="text-primary hover:text-primary/80 underline">{{ t('cookies.policy') }}</a> 
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <button @click="rejectCookies" 
                      class="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {{ t('cookies.reject') }}
              </button>
              <button @click="acceptCookies" 
                      class="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                {{ t('cookies.accept') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Footer -->
    <footer class="bg-white dark:bg-neutral-dark relative">
      <!-- Back to Top Button -->
      <button @click="scrollToTop" 
              class="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-primary text-white shadow-lg hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center transform hover:scale-110 z-50"
              :class="{ 'translate-y-20': !showBackToTop, 'translate-y-0': showBackToTop }">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
        </svg>
      </button>

      <!-- Düz çizgi ve yarım daire efekti -->
      <div class="absolute top-2 left-0 w-full h-[3px] bg-primary dark:bg-primary">
        <div class="absolute left-1/2 -translate-x-1/2 w-32 h-16 overflow-hidden -top-0">
          <div class="w-32 h-32 border-[3px] border-primary dark:border-primary rounded-full bg-white dark:bg-neutral-dark -translate-y-16"></div>
        </div>
      </div>

      <!-- Logo Container -->
      <div class="absolute left-1/2 -translate-x-1/2 -top-12 z-10">
        <div class="w-28 h-28 bg-white dark:bg-neutral-dark rounded-full flex items-center justify-center shadow-xl border-4 border-gray-50 dark:border-neutral-800 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
          <img src="/assets/images/tp-logo-footer.png" alt="Footer Logo" class="w-20 h-20 object-contain" />
        </div>
      </div>

      <div class="container mx-auto px-4 py-16">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pt-8">
          <!-- Hızlı Linkler -->
          <div class="text-center md:text-left">
            <div class="relative pb-4 mb-6 flex flex-col items-center md:items-start">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('footer.quickLinks') }}</h3>
              <div class="w-12 h-0.5 bg-primary mt-4"></div>
            </div>
            <div class="space-y-3">
              <NuxtLink v-for="link in quickLinks" 
                       :key="link.path" 
                       :to="link.path" 
                       class="block text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors">
                {{ link.title }}
              </NuxtLink>
            </div>
          </div>

          <!-- İletişim -->
          <div class="text-center md:text-left">
            <div class="relative pb-4 mb-6 flex flex-col items-center md:items-start">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('footer.contact') }}</h3>
              <div class="w-12 h-0.5 bg-primary mt-4"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-center justify-center md:justify-start space-x-3 text-gray-600 dark:text-gray-400">
                <svg class="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span>{{ contactInfo.address }}</span>
              </div>
              <div class="flex items-center justify-center md:justify-start space-x-3 text-gray-600 dark:text-gray-400">
                <svg class="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <a href="mailto:info@example.com" class="hover:text-primary transition-colors">{{ contactInfo.email }}</a>
              </div>
              <div class="flex items-center justify-center md:justify-start space-x-3 text-gray-600 dark:text-gray-400">
                <svg class="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <a href="tel:+905551234567" class="hover:text-primary transition-colors">{{ contactInfo.phone }}</a>
              </div>
            </div>
          </div>

          <!-- Sosyal Medya -->
          <div class="text-center md:text-left">
            <div class="relative pb-4 mb-6 flex flex-col items-center md:items-start">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('footer.followUs') }}</h3>
              <div class="w-12 h-0.5 bg-primary mt-4"></div>
            </div>
            <div class="flex items-center justify-center md:justify-start space-x-4">
              <a v-if="socialMedia.facebook" :href="socialMedia.facebook" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white dark:hover:bg-primary transition-all duration-300">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a v-if="socialMedia.twitter" :href="socialMedia.twitter" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white dark:hover:bg-primary transition-all duration-300">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a v-if="socialMedia.linkedin" :href="socialMedia.linkedin" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white dark:hover:bg-primary transition-all duration-300">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a v-if="socialMedia.instagram" :href="socialMedia.instagram" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white dark:hover:bg-primary transition-all duration-300">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a v-if="socialMedia.whatsapp" :href="`https://wa.me/${socialMedia.whatsapp.replace(/[^0-9]/g, '')}`" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary hover:text-white dark:hover:bg-primary transition-all duration-300">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Mail Subscription -->
          <div class="text-center md:text-left">
            <div class="relative pb-4 mb-6 flex flex-col items-center md:items-start">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('footer.newsletter') }}</h3>
              <div class="w-12 h-0.5 bg-primary mt-4"></div>
            </div>
            <p class="text-gray-600 dark:text-gray-400 mb-4">{{ t('footer.newsletterDesc') }}</p>
            <form @submit.prevent="submitNewsletterForm" class="space-y-3 max-w-sm mx-auto md:mx-0">
              <div class="flex shadow-sm">
                <input type="email" 
                       v-model="newsletterEmail"
                       placeholder="E-posta adresiniz" 
                       class="flex-1 px-4 py-3 rounded-l-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                       :disabled="isNewsletterLoading" />
                <button type="submit" 
                        class="px-6 py-3 bg-primary text-white rounded-r-lg hover:bg-opacity-90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        :disabled="isNewsletterLoading">
                  <span v-if="!isNewsletterLoading">{{ t('footer.join') }}</span>
                  <span v-else class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ t('footer.sending') }}
                  </span>
                </button>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ t('footer.privacyAccept') }}
              </p>
            </form>
          </div>
        </div>

        <!-- Alt Bilgi -->
        <div class="border-t border-primary dark:border-primary mt-12 pt-8">
          <div class="text-center">
            <p class="text-gray-600 dark:text-gray-400">
              © {{ new Date().getFullYear() }} {{ siteSettings.site_title }}. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </footer>

    <!-- Sayfa Geçiş Animasyonu -->
    <div v-if="showTransition" 
         :style="transitionStyle"
         class="fixed transition-all duration-700 ease-in-out rounded-full bg-[#ed194c] z-[9999]">
    </div>
    
    <!-- Gizlilik Politikası Modal -->
    <PrivacyPolicyModal 
      v-model="showPrivacyModal"
      :policy-content="policies.cookie_policy"
    />
  </div>
</template>

<script setup lang="ts">
import 'flag-icons/css/flag-icons.min.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import { ChevronDownIcon, ChevronRightIcon, SunIcon, MoonIcon, Bars3Icon, XMarkIcon, CalendarIcon } from '@heroicons/vue/24/outline'
import { useSettings } from '~/composables/useSettings'
import { useAnnouncement } from '~/composables/useAnnouncement'
import { useNewsletter } from '~/composables/useNewsletter'
import { useMenu } from '~/composables/useMenu'
import PageService from '~/services/PageService'
import PrivacyPolicyModal from '~/components/PrivacyPolicyModal.vue'

const { locale, locales, t } = useI18n()
const router = useRouter()
const switchLocalePath = useSwitchLocalePath()
const colorMode = useColorMode()
const isMenuOpen = ref(false)
const isLangOpen = ref(false)
const scrolled = ref(false)
const showBackToTop = ref(false)
const cookiesAccepted = ref(false)
const showPrivacyModal = ref(false)

// Site ayarlarını yükle
const { contactInfo, socialMedia, siteSettings, policies } = useSettings()

// Duyuruları yükle
const { activeAnnouncements, hasActiveAnnouncements } = useAnnouncement()

// Newsletter composable'ını kullan
const { email: newsletterEmail, isLoading: isNewsletterLoading, subscribe } = useNewsletter()

const localePath = useLocalePath()

const { menuItems: dynamicMenuItems, isLoading: isMenuLoading, error: menuError } = useMenu()

// Menü yapılandırması - fallback olarak statik yapı kullanılıyor
const menuItems = computed(() => {
  // Eğer API'den menü verileri gelmiş ve hata yoksa, dinamik menüyü kullan
  if (dynamicMenuItems.value && dynamicMenuItems.value.length > 0 && !menuError.value) {
    // API'den gelen menü öğelerinin işlenmesi ve uygun formata dönüştürülmesi
    return formatMenuItems(dynamicMenuItems.value)
  }
  
  // Fallback olarak statik menü yapısı (API'den veri gelmemesi durumunda)
  return [
    {
      title: 'Ana Sayfa',
      path: localePath('index'),
      isOpen: false
    },
    {
      title: 'Hakkımızda',
      path: localePath('about'),
      isOpen: false,
      children: [
        { path: localePath('about/history'), title: 'Tarihçemiz', isOpen: false },
        { path: localePath('about/mission'), title: 'Misyon & Vizyon', isOpen: false },
        { 
          path: localePath('about/team'), 
          title: 'Ekibimiz',
          isOpen: false,
          children: [
            { path: localePath('about/team/management'), title: 'Yönetim' },
            { path: localePath('about/team/experts'), title: 'Uzmanlarımız' }
          ]
        },
        { path: localePath('about/partners'), title: 'Ortaklarımız' }
      ]
    },
    {
      title: 'Firmalar',
      path: localePath('companies-all'),
      isOpen: false
    },
    { 
      path: localePath('services'), 
      title: 'Hizmetlerimiz',
      isOpen: false,
      children: [
        { path: localePath('services-rental-fees'), title: 'Kira Hizmet Bedelleri' },
        { path: localePath('services-conference-room'), title: 'Konferans Salonu Talebi' }
      ]
    },
    {
      title: 'CV Havuzu',
      path: localePath('cv-pool'),
      isOpen: false
    },
    {
      title: 'Haberler',
      path: localePath('news'),
      isOpen: false
    },
    {
      title: 'İletişim',
      path: localePath('contact'),
      isOpen: false
    }
  ]
})

// API'den gelen menü verilerini uygulamada kullanılacak formata dönüştürür
const formatMenuItems = (apiMenuItems: any[]) => {
  console.log('API Menü Öğeleri:', apiMenuItems);
  
  // URL'nin harici bir adres olup olmadığını kontrol eden yardımcı fonksiyon
  const isExternalUrl = (url: string): boolean => {
    if (!url) return false;
    return url.startsWith('http://') || 
           url.startsWith('https://') || 
           url.startsWith('mailto:') || 
           url.startsWith('tel:') ||
           url.startsWith('#');
  };
  
  // URL'yi işleyen fonksiyon - harici ise olduğu gibi, dahili ise localePath ile
  const processUrl = (url: string | null, pageId: number | null | string | undefined, pageUrl: string | null = null): string => {
    // URL varsa ve harici bir adres ise olduğu gibi döndür
    if (url && isExternalUrl(url)) {
      return url;
    } 
    
    // URL varsa ve dahili ise localePath kullan
    if (url) {
      // URL başında / varsa kaldır (localePath için)
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
      return localePath(cleanUrl);
    }
    
    // Eğer page_url varsa öncelikle onu kullan
    if (pageUrl) {
      return localePath({
        name: 'pages-slug',
        params: { 
          slug: [pageUrl]
        }
      });
    }
    
    // Page ID varsa sayfa slug'ını kullan
    if (pageId) {
      return localePath({
        name: 'pages-slug',
        params: { 
          slug: [pageId.toString()]
        }
      });
    }
    
    // Hiçbir şey bulunamazsa # döndür
    return '#';
  };
  
  // API'den gelen menüyü uygulamada kullanılacak formata dönüştür
  return apiMenuItems
    .filter(item => !item.parent_id) // Sadece ana menü öğelerini al
    .map(rootItem => {
      // Path oluştur
      const path = processUrl(rootItem.url, rootItem.page_id, rootItem.page_url);
      
      // Alt menü öğelerini dönüştür (eğer varsa)
      const children = rootItem.children?.map((child: any) => {
        const childPath = processUrl(child.url, child.page_id, child.page_url);
        
        // Alt-alt menü öğelerini dönüştür (eğer varsa)
        const grandChildren = child.children?.map((grandChild: any) => {
          const grandChildPath = processUrl(grandChild.url, grandChild.page_id, grandChild.page_url);
          return {
            title: grandChild.name,
            path: grandChildPath,
            isOpen: false
          };
        });
        
        return {
          title: child.name,
          path: childPath,
          isOpen: false,
          children: grandChildren && grandChildren.length > 0 ? grandChildren : undefined
        };
      });
      
      return {
        title: rootItem.name,
        path,
        isOpen: false,
        children: children && children.length > 0 ? children : undefined
      };
    });
}

const availableLocales = computed(() => {
  return (locales.value).filter(i => i.code !== locale.value)
})

const currentLocaleName = computed(() => {
  return (locales.value).find(i => i.code === locale.value)?.name
})

const switchLocale = async (code: string) => {
  try {
    isLangOpen.value = false
    const path = switchLocalePath(code)
    if (path) {
      // Sayfa yönlendirmesi yerine sayfayı yenile
      localStorage.setItem('preferred_locale', code)
      window.location.href = path
    }
  } catch (error) {
    console.error('Dil değiştirme hatası:', error)
  }
}

const toggleColorMode = () => {
  const newMode = colorMode.value === 'dark' ? 'light' : 'dark'
  colorMode.preference = newMode
  document.documentElement.classList.toggle('dark')
}

// Back to Top fonksiyonu
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// Sayfa scroll olduğunda header'ı güncelle
onMounted(() => {
  if (colorMode.preference === 'dark') {
    document.documentElement.classList.add('dark')
  }
  
  window.addEventListener('scroll', () => {
    scrolled.value = window.scrollY > 0
    showBackToTop.value = window.scrollY > 500
  })

  const cookieStatus = localStorage.getItem('cookiesAccepted')
  if (cookieStatus === 'true') {
    cookiesAccepted.value = true
  }

  // Duyurular varsa ve hiç gösterilmediyse modalı göster
  watchEffect(() => {
    if (hasActiveAnnouncements.value) {
      // Herhangi bir duyuru daha önce görülmemiş mi kontrol et
      const hasUnseenAnnouncement = activeAnnouncements.value.some(announcement => {
        return !localStorage.getItem(`announcement_${announcement.id}`)
      })
      
      if (hasUnseenAnnouncement) {
        // İlk gösterilmemiş duyuruyu bul ve indexini ayarla
        const firstUnseenIndex = activeAnnouncements.value.findIndex(announcement => {
          return !localStorage.getItem(`announcement_${announcement.id}`)
        })
        
        if (firstUnseenIndex !== -1) {
          currentAnnouncementIndex.value = firstUnseenIndex
          showAnnouncementModal.value = true
        }
      }
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', () => {
    scrolled.value = window.scrollY > 0
    showBackToTop.value = window.scrollY > 500
  })
})

// Sayfa yönlendirmelerinde menüyü kapat
watch(() => router.currentRoute.value.path, () => {
  isMenuOpen.value = false
})

// Mobil menü için toggle fonksiyonları
const toggleMobileMenu = (item: any) => {
  if (!item.children) {
    navigateTo(item.path)
    isMenuOpen.value = false
    return
  }
  item.isOpen = !item.isOpen
}

const toggleMobileSubmenu = (item: any) => {
  if (!item.children) {
    navigateTo(item.path)
    isMenuOpen.value = false
    return
  }
  item.isOpen = !item.isOpen
}

// Footer için hızlı bağlantılar
const quickLinks = [
  { title: 'Ana Sayfa', path: localePath('index') },
  { title: 'Hakkımızda', path: localePath('about') },
  { title: 'Hizmetlerimiz', path: localePath('services') },
  { title: 'CV Havuzu', path: localePath('cv-pool') },
  { title: 'Sık Sorulan Sorular', path: localePath('faq') },
  { title: 'İletişim', path: localePath('contact') }
]

// Çerezleri kabul et
const acceptCookies = () => {
  cookiesAccepted.value = true
  localStorage.setItem('cookiesAccepted', 'true')
}

// Çerezleri reddet
const rejectCookies = () => {
  cookiesAccepted.value = true // Banner'ı kaldır
  localStorage.setItem('cookiesAccepted', 'false')
}

// Duyuru modalı için state'ler
const showAnnouncementModal = ref(false)
const dontShowAgain = ref(false)
const currentAnnouncementIndex = ref(0)

// Aktif duyuru
const currentAnnouncement = computed(() => {
  if (hasActiveAnnouncements.value && activeAnnouncements.value.length > currentAnnouncementIndex.value) {
    return {
      id: activeAnnouncements.value[currentAnnouncementIndex.value].id,
      title: activeAnnouncements.value[currentAnnouncementIndex.value].title,
      description: activeAnnouncements.value[currentAnnouncementIndex.value].content,
      date: new Date(activeAnnouncements.value[currentAnnouncementIndex.value].created_at).toLocaleDateString('tr-TR')
    }
  }
  
  return {
    id: '',
    title: '',
    description: '',
    date: ''
  }
})

// Bir sonraki duyuruya geç
const nextAnnouncement = () => {
  if (activeAnnouncements.value.length > currentAnnouncementIndex.value + 1) {
    currentAnnouncementIndex.value++;
  } else {
    // Son duyuruya gelindi, modalı kapat
    closeAnnouncementModal();
  }
}

// Bir önceki duyuruya dön
const prevAnnouncement = () => {
  if (currentAnnouncementIndex.value > 0) {
    currentAnnouncementIndex.value--;
  }
}

// Duyuru modalını kapat
const closeAnnouncementModal = () => {
  if (dontShowAgain.value) {
    // Tüm aktif duyurular için dismissed olarak işaretle
    activeAnnouncements.value.forEach(announcement => {
      localStorage.setItem(`announcement_${announcement.id}`, 'dismissed')
    })
  }
  showAnnouncementModal.value = false
}

// Sayfa geçiş animasyonu için state'ler
const showTransition = ref(false)
const transitionStyle = ref({})

// Sayfa geçiş animasyonu başlat
const startTransition = (event: MouseEvent) => {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  const maxDistance = Math.sqrt(Math.pow(screenWidth, 2) + Math.pow(screenHeight, 2))
  const scale = (maxDistance * 2) / 10
  
  transitionStyle.value = {
    position: 'fixed',
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
    width: '10px',
    height: '10px',
    transform: 'scale(1)',
    transformOrigin: 'center center',
    opacity: '1'
  }
  
  showTransition.value = true
  
  requestAnimationFrame(() => {
    transitionStyle.value = {
      ...transitionStyle.value,
      transform: `scale(${scale})`,
      opacity: '1'
    }
  })
  
  setTimeout(() => {
    window.location.href = 'https://www.nargirisimofisi.com/'
  }, 700)
}

// Newsletter form gönderimi
const submitNewsletterForm = async () => {
  await subscribe();
};
</script>

<style>
.fi {
  width: 1.5em;
  line-height: 1em;
}

/* Dropdown animasyonları için ek stiller */
.group:hover .group-hover\:translate-y-0 {
  --tw-translate-y: 0;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

.translate-y-2 {
  --tw-translate-y: 0.5rem;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

/* Dark mode için ek stiller */
:root {
  --primary-bg: #009AB2;
  --primary-color: #009AB2;
  --primary-text: #ffffff;
}

.dark {
  --primary-bg: #004D56;
  --primary-text: #ffffff;
}

.text-primary {
  color: var(--primary-bg);
}

.bg-primary {
  background-color: var(--primary-bg);
}

.hover\:text-primary:hover {
  color: var(--primary-bg);
}

.hover\:bg-primary:hover {
  background-color: var(--primary-bg);
}

/* Drawer animasyonları için ek stiller */
.translate-x-full {
  transform: translateX(100%);
}

.translate-x-0 {
  transform: translateX(0);
}

/* Menü hover davranışı için ek stiller */
.group\/child > div,
.group\/item > div {
  pointer-events: none;
}

.group\/child:hover > div,
.group\/item:hover > div {
  pointer-events: auto;
}

/* Hover efektleri için ek stiller */
.group\/item:hover .bg-primary,
.group\/subchild:hover .bg-primary {
  transform: scaleY(1);
}

.group\/item:hover .bg-gray-50,
.group\/subchild:hover .bg-gray-50 {
  opacity: 1;
}
</style> 